/**
 * JSON-LD structured data.
 *
 * Tells a search engine what this page is in a form it parses exactly, rather
 * than inferring. Two types, joined into one graph:
 *
 *   ProfessionalService  what the studio sells, where, and in what languages
 *   Organization         the entity itself, linked to its GitHub and LinkedIn
 *                        via sameAs so the two are understood as one thing
 *
 * `SoftwareApplication` is deliberately absent. It belongs on the product
 * sites, which each describe one app; this page describes a studio, and
 * claiming an application here would be a claim about software this URL does
 * not offer.
 *
 * The prices are not marked up either. `PriceSpecification` on a service whose
 * real number depends on a written scope would be a machine-readable promise
 * the contract does not make.
 */
import { useI18n } from '../i18n';
import { COMPANY, SITE_ORIGIN } from '../lib/constants';

export function StructuredData() {
  const { t, locale } = useI18n();

  const data = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'ProfessionalService',
        '@id': `${SITE_ORIGIN}/#studio`,
        name: COMPANY.tradeName,
        url: SITE_ORIGIN,
        description: t.meta.description,
        email: COMPANY.email,
        inLanguage: locale,
        availableLanguage: ['en', 'es'],
        areaServed: ['MX', 'US'],
        foundingDate: COMPANY.founded,
        knowsAbout: [
          'Python',
          'Django',
          'Flask',
          'Node.js',
          'React',
          'TypeScript',
          'Flutter',
          'Odoo',
          'PostgreSQL',
          'REST APIs',
        ],
        parentOrganization: { '@id': `${SITE_ORIGIN}/#organization` },
      },
      {
        '@type': 'Organization',
        '@id': `${SITE_ORIGIN}/#organization`,
        name: COMPANY.legalName,
        url: SITE_ORIGIN,
        email: COMPANY.email,
        sameAs: [COMPANY.github, COMPANY.linkedin],
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      // The content is our own constant object, not anything a visitor can
      // influence, so there is no injection surface here.
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}
